import { useCallback, useRef, useState } from 'preact/hooks';
import type { ChatMessage, ChatRole, ProductCard } from '@sophia-ai/shared-types';
import { getSessionId } from '../lib/session';
import { sendMessage } from '../lib/chatClient';

/** Lifecycle of an assistant turn as the UI renders it. */
export type MessageStatus = 'streaming' | 'done' | 'error';

/** One visible turn, enriched with streamed state and product cards. */
export interface UiMessage {
  id: string;
  role: ChatRole;
  text: string;
  products: ProductCard[];
  status: MessageStatus;
}

export interface UseChat {
  messages: UiMessage[];
  busy: boolean;
  send: (text: string) => void;
  /** Re-run the last user message after a failed turn. */
  retry: () => void;
}

/**
 * Owns the conversation: appends turns, streams the assistant reply in place,
 * and reports busy state. Prior completed turns are sent as `history` so the
 * server can reconstruct context.
 */
export function useChat(apiBaseUrl: string): UseChat {
  const [messages, setMessages] = useState<UiMessage[]>([]);
  const messagesRef = useRef<UiMessage[]>([]);
  const busyRef = useRef(false);
  const [busy, setBusy] = useState(false);

  // Single source of truth: compute the next list from the ref, then publish.
  const apply = useCallback((updater: (prev: UiMessage[]) => UiMessage[]): void => {
    const next = updater(messagesRef.current);
    messagesRef.current = next;
    setMessages(next);
  }, []);

  // Issue one request for `userText` (the user turn is already in the list) and
  // stream the assistant reply into a fresh turn. Shared by send and retry.
  const execute = useCallback(
    (userText: string, history: ChatMessage[]): void => {
      const assistantId = crypto.randomUUID();

      apply((prev) => [
        ...prev,
        { id: assistantId, role: 'assistant', text: '', products: [], status: 'streaming' },
      ]);

      const patch = (id: string, change: Partial<UiMessage>): void => {
        apply((prev) =>
          prev.map((message) => (message.id === id ? { ...message, ...change } : message)),
        );
      };

      busyRef.current = true;
      setBusy(true);

      void sendMessage(
        apiBaseUrl,
        { sessionId: getSessionId(), message: userText, history },
        {
          onDelta: (chunk) => {
            const current = messagesRef.current.find((message) => message.id === assistantId);
            patch(assistantId, { text: (current?.text ?? '') + chunk });
          },
          onDone: (reply) => {
            patch(assistantId, { text: reply.text, products: reply.products, status: 'done' });
          },
          onError: (message) => {
            // Keep any partial text already streamed; otherwise show the error.
            const existing =
              messagesRef.current.find((entry) => entry.id === assistantId)?.text ?? '';
            patch(assistantId, { text: existing !== '' ? existing : message, status: 'error' });
          },
        },
      ).finally(() => {
        busyRef.current = false;
        setBusy(false);
      });
    },
    [apiBaseUrl, apply],
  );

  // Completed, non-empty turns only, oldest first — the context the server
  // replays. Empty turns (e.g. a cards-only assistant reply) are dropped so we
  // never send an empty message back as history.
  const history = (list: UiMessage[]): ChatMessage[] =>
    list
      .filter((message) => message.status === 'done' && message.text.trim() !== '')
      .map((message) => ({ role: message.role, text: message.text }));

  const send = useCallback(
    (raw: string): void => {
      const text = raw.trim();

      if (text === '' || busyRef.current) {
        return;
      }

      const prior = history(messagesRef.current);

      apply((prev) => [
        ...prev,
        { id: crypto.randomUUID(), role: 'user', text, products: [], status: 'done' },
      ]);

      execute(text, prior);
    },
    [apply, execute],
  );

  const retry = useCallback((): void => {
    if (busyRef.current) {
      return;
    }

    const list = messagesRef.current;

    let index = -1;
    for (let i = list.length - 1; i >= 0; i--) {
      if (list[i]?.role === 'user') {
        index = i;
        break;
      }
    }

    const userMessage = index >= 0 ? list[index] : undefined;
    if (userMessage === undefined) {
      return;
    }

    const prior = history(list.slice(0, index));
    // Drop the failed assistant turn(s) after the user message, then re-run.
    apply((prev) => prev.slice(0, index + 1));
    execute(userMessage.text, prior);
  }, [apply, execute]);

  return { messages, busy, send, retry };
}
